/**
 * `AW4` · Formulario de filtros de la tabla «Conversaciones».
 *
 * Esta pantalla es pura presentación: convierte lo que el operador escribe en el
 * filtro que espera el contrato (`FiltroDeMensajes`) y se lo pasa al llamador a
 * través de `onAplicar`. No guarda estado global ni habla con `admin-web`.
 */

import { useState } from "react"
import type { JSX } from "react"
import type { FiltroDeLaPantalla } from "./borde-conversaciones.ts"

/**
 * Borrador que vive en el formulario. Todos son `string` porque los controles
 * nativos (`<input type="date">` y `<input type="text">`) trabajan con texto.
 *
 * ⭐ `R3-UI1` · ~~«La conversión a `Date | null` ocurre sólo al apretar “Aplicar
 * filtro”»~~ — **ya no hay conversión a `Date`**: ver ⟦los-dias-son-dias⟧ abajo.
 */
type Borrador = {
  readonly desde: string
  readonly hasta: string
  readonly telefono: string
  /** ⭐ `R3-UI1` · El cuarto filtro, `PRD_R3.md:692`. */
  readonly contenido: string
}

const BORRADOR_VACIO: Borrador = {
  desde: "",
  hasta: "",
  telefono: "",
  contenido: "",
}

/** El filtro que significa «mostrar todo el brazo»: los cuatro campos en `null`. */
const SIN_NADA_APLICADO: FiltroDeLaPantalla = {
  desde: null,
  hasta: null,
  telefono: null,
  contenido: null,
}

/**
 * ⟦el-cuarto-campo-del-filtro⟧ · `contracts/conversation` (`R3-CCV1`) sumó el
 * CUARTO campo del filtro, `contenido`, obligatorio y anulable como los otros
 * tres. ⭐ `R3-UI1` lo dibuja.
 *
 * ⛔ **`null` NO es un dato inventado: es la única forma de decir «no filtra»**
 * que el contrato admite (*«acá `null` es la única forma de “no filtra”, igual que
 * en los otros tres campos»*). Un `""` en su lugar sería un filtro «coincide con
 * la cadena vacía», que del otro lado es otra pregunta.
 */
/**
 * ⭐⭐ ⟦los-dias-son-dias⟧ · `R3-UI1` · **RT 3: Desde y Hasta viajan como DÍAS, y
 * la pantalla no construye ningún `Date`.**
 *
 * ~~`new Date("…T00:00:00")` / `new Date("…T23:59:59.999")`~~ — lo que había
 * armaba los dos instantes **en la zona del NAVEGADOR**. `PRD_R3.md:1028` dice
 * que la demo corre con el navegador en **UTC**, y `PRD_R3.md:688`: *«Un mensaje
 * de las 23:30 de ese día entra aunque en UTC ya sea el día siguiente»* — o sea
 * que el defecto no es teórico: corría el borde del día una hora o tres y NADIE
 * se enteraba, porque el filtro contestaba filas perfectamente válidas.
 *
 * ⛔ **Y NO se arregla pegándole `-03:00` al literal**, que es lo que uno escribe
 * primero: es el `rationale_hint` textual del guard RT 3 —*«se rompe en el horario
 * de verano de Chile»*—. Chile cambia de hora; un desplazamiento fijo se equivoca
 * medio año, y el día que se equivoca no hay ningún error.
 *
 * ⇒ viaja el valor CRUDO del `<input type="date">`, que el estándar fija en
 * `aaaa-mm-dd` **sin importar en qué formato lo muestre el navegador**
 * (`PRD_R3.md:686`, el campo puede verse `09/14/2026` en inglés de EE.UU.). Quien
 * lo convierte a las 00:00:00/23:59:59 de `America/Santiago` es `admin-web`
 * (`R3-AW1`), que es el único lado que tiene la zona.
 *
 * ⚠️ **El recorte de Contenido lo hace QUIEN LLAMA, y es del contrato:** *«el
 * contrato pide que quien llama recorte los extremos y mande `null` cuando no
 * queda nada, para que el módulo no tenga que decidir si `"   "` filtra»*. Por eso
 * se recorta acá y no allá — y por eso «  becas  » encuentra «becas».
 */
function filtroDesdeBorrador(borrador: Borrador): FiltroDeLaPantalla {
  /*
   * El teléfono se recorta para decidir si HAY filtro, pero lo que VIAJA es lo
   * tipeado tal cual: `PRD_R3.md:691`, *«La pantalla no corrige lo escrito»*.
   * El contenido, en cambio, viaja recortado — lo pide el contrato.
   */
  const contenido = borrador.contenido.trim()
  return {
    desde: borrador.desde.trim() === "" ? null : borrador.desde,
    hasta: borrador.hasta.trim() === "" ? null : borrador.hasta,
    telefono: borrador.telefono.trim() === "" ? null : borrador.telefono,
    contenido: contenido === "" ? null : contenido,
  }
}

/**
 * Filtros de la tabla de conversaciones.
 *
 * ⭐ `guard` · **el filtro por teléfono es de IGUALDAD EXACTA y la pantalla NO lo
 * presenta como una búsqueda.** El campo no es un buscador que filtra mientras
 * se tipea; el operador escribe el identificador COMPLETO y aprieta «Aplicar
 * filtro». Una búsqueda por subcadena convertiría esta pantalla en una
 * superficie de **enumeración** —tres caracteres y aparecen todos—, que cambia
 * qué puede hacer una de las ~10 personas con acceso a datos de menores. El
 * texto de ayuda lo dice, para que el operador no invente resultados parciales.
 */
export function FiltrosDeConversaciones(props: {
  readonly onAplicar: (filtro: FiltroDeLaPantalla) => void
}): JSX.Element {
  const [borrador, setBorrador] = useState<Borrador>(BORRADOR_VACIO)

  function aplicar(): void {
    props.onAplicar(filtroDesdeBorrador(borrador))
  }

  /**
   * «Limpiar» vacía los CUATRO y vuelve a mostrar todo el brazo (regla 8,
   * `PRD_R3.md:861`). Manda `SIN_NADA_APLICADO` y no `filtroDesdeBorrador(
   * BORRADOR_VACIO)`: el borrador vacío y «sin filtro» tienen que ser lo mismo,
   * y escribirlo dos veces es dos lugares donde pueden dejar de serlo.
   */
  function limpiar(): void {
    setBorrador(BORRADOR_VACIO)
    props.onAplicar(SIN_NADA_APLICADO)
  }

  return (
    <form className="conversaciones__filtros" onSubmit={(e) => e.preventDefault()}>
      <div className="conversaciones__filtro">
        <label className="conversaciones__etiqueta" htmlFor="conversaciones-desde">
          Desde
        </label>
        <input
          id="conversaciones-desde"
          type="date"
          value={borrador.desde}
          onChange={(e) => setBorrador((prev) => ({ ...prev, desde: e.target.value }))}
        />
      </div>

      <div className="conversaciones__filtro">
        <label className="conversaciones__etiqueta" htmlFor="conversaciones-hasta">
          Hasta
        </label>
        <input
          id="conversaciones-hasta"
          type="date"
          value={borrador.hasta}
          onChange={(e) => setBorrador((prev) => ({ ...prev, hasta: e.target.value }))}
        />
      </div>

      <div className="conversaciones__filtro">
        <label className="conversaciones__etiqueta" htmlFor="conversaciones-telefono">
          Teléfono
        </label>
        <input
          id="conversaciones-telefono"
          type="text"
          value={borrador.telefono}
          onChange={(e) => setBorrador((prev) => ({ ...prev, telefono: e.target.value }))}
        />
        <span className="conversaciones__ayuda">
          Identificador completo: la búsqueda es por coincidencia exacta, no por fragmento.
        </span>
      </div>

      {/*
        ⭐ `R3-UI1` · **EL CUARTO FILTRO.** `R3-F1-CA-08` pide exactamente cuatro
        —Desde, Hasta, Teléfono y Contenido— y `PRD_R3.md:695` agrega la mitad que
        un examen de presencia no ve: *«No hay filtro por estado del mensaje ni por
        tipo de fila»*.

        ⚠️ Es `type="text"` y no `type="search"`: el control de búsqueda del
        navegador trae su propia «x» de borrado y su propio comportamiento de
        Enter, y los cuatro filtros de esta pantalla se aplican **sólo** con
        «Aplicar filtro» (regla 8). Un campo que se vacía solo con la «x» del
        navegador dejaría la tabla mostrando un filtro que el campo ya no dice.
      */}
      <div className="conversaciones__filtro">
        <label className="conversaciones__etiqueta" htmlFor="conversaciones-contenido">
          Contenido
        </label>
        <input
          id="conversaciones-contenido"
          type="text"
          value={borrador.contenido}
          onChange={(e) => setBorrador((prev) => ({ ...prev, contenido: e.target.value }))}
        />
        {/*
          La ayuda es TEXTUAL del PRD (`:692`) y dice lo que el SERVIDOR hace: el
          plegado de mayúsculas y tildes corre en `modules/conversation` (RT 2), no
          acá. La pantalla no pliega nada — si lo hiciera, serían dos plegados que
          divergen, que es la especie que `R3-F1-CA-108` cierra del otro lado.
        */}
        <span className="conversaciones__ayuda">Sin distinguir mayúsculas ni tildes.</span>
      </div>

      <div className="conversaciones__acciones">
        <button type="button" onClick={aplicar}>
          Aplicar filtro
        </button>
        <button type="button" onClick={limpiar}>
          Limpiar
        </button>
      </div>
    </form>
  )
}
